import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Utensils, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
      toast.success('Welkom terug!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Inloggen mislukt. Controleer je gegevens.');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    setLoading(true);
    try {
      await login('demo@food-flows.com', 'demo123');
      toast.success('Welkom bij de demo!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Demo inloggen mislukt. Probeer het opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8" data-testid="logo-link">
            <div className="w-10 h-10 bg-[#1A4D2E] rounded-xl flex items-center justify-center">
              <Utensils className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-xl text-[#1A4D2E]">FoodFlows</span>
          </Link>

          <h1 className="font-heading text-3xl font-bold text-[#1F2937] mb-2">Welkom terug</h1>
          <p className="text-gray-600 mb-8">Log in om je restaurant te beheren</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">E-mailadres</Label>
              <Input
                id="email"
                type="email"
                placeholder="jij@restaurant.nl"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Wachtwoord</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Voer je wachtwoord in"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 pr-12"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full btn-primary h-12"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Inloggen...' : 'Inloggen'}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-[#F8F9FA] px-4 text-gray-500">of</span>
            </div>
          </div>

          <Button 
            variant="outline" 
            className="w-full h-12 border-2 border-gray-200 hover:border-[#1A4D2E] hover:bg-[#D8F3DC]/20"
            onClick={handleDemoLogin}
            disabled={loading}
            data-testid="demo-login-btn"
          >
            Probeer Demo Account
          </Button>

          <p className="mt-8 text-center text-gray-600">
            Nog geen account?{' '}
            <Link to="/register" className="text-[#1A4D2E] font-medium hover:underline" data-testid="register-link">
              Gratis registreren
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.pexels.com/photos/7133566/pexels-photo-7133566.jpeg"
          alt="Restaurant eten"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A4D2E]/80 to-[#1A4D2E]/40"></div>
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <blockquote className="font-accent text-2xl italic mb-4">
            "FoodFlows heeft de manier waarop we bestellingen beheren getransformeerd. Onze efficiëntie is met 40% gestegen!"
          </blockquote>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full"></div>
            <div>
              <p className="font-medium">Peter de Vries</p>
              <p className="text-sm text-white/80">Eigenaar, De Gouden Vork</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    address: '',
    postalCode: '',
    city: '',
    vatNumber: '',
    cuisineType: '',
  });
  const [veldFouten, setVeldFouten] = useState({});
  const [akkoord, setAkkoord] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (veldFouten[e.target.name]) {
      setVeldFouten({ ...veldFouten, [e.target.name]: null });
    }
  };

  // Belgisch ondernemingsnummer: BE + 0 + negen cijfers, waarbij de laatste
  // twee cijfers 97 min de eerste acht modulo 97 zijn. Dezelfde controle draait
  // in de backend; hier alleen om de fout meteen te tonen.
  const btwGeldig = (waarde) => {
    let schoon = String(waarde || '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();
    if (/^\d{10}$/.test(schoon)) schoon = 'BE' + schoon;
    if (!/^BE0\d{9}$/.test(schoon)) return false;
    const cijfers = schoon.slice(2);
    return (97 - (parseInt(cijfers.slice(0, 8), 10) % 97)) === parseInt(cijfers.slice(8), 10);
  };

  const valideer = () => {
    const f = {};
    if (!formData.name.trim()) f.name = 'Vul de naam van je restaurant in';
    if (!formData.phone.trim()) f.phone = 'Vul een telefoonnummer in';
    if (!formData.address.trim()) f.address = 'Vul straat en huisnummer in';
    else if (!/\d/.test(formData.address)) f.address = 'Vul ook het huisnummer in';
    if (!/^[1-9]\d{3}$/.test(formData.postalCode.trim())) f.postalCode = 'Vier cijfers, bijvoorbeeld 2990';
    if (!formData.city.trim()) f.city = 'Vul je gemeente in';
    if (!btwGeldig(formData.vatNumber)) f.vatNumber = 'Formaat BE0xxxxxxxxx, controlegetal moet kloppen';
    if (!akkoord) f.akkoord = 'Je moet akkoord gaan om een account aan te maken';
    setVeldFouten(f);
    return Object.keys(f).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valideer()) {
      toast.error('Controleer de gemarkeerde velden');
      return;
    }
    setLoading(true);

    try {
      const res = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        address: formData.address,
        postal_code: formData.postalCode,
        city: formData.city,
        vat_number: formData.vatNumber,
        cuisine_type: formData.cuisineType,
        accept_legal: akkoord,
      });
      toast.success(res?.message
        || 'Account aangemaakt. Bevestig je e-mailadres om je bestelpagina online te zetten.',
        { duration: 8000 });
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      // FastAPI stuurt bij validatiefouten een lijst met veld + melding.
      if (Array.isArray(detail)) {
        toast.error(detail.map((d) => d.msg?.replace('Value error, ', '')).join(' · '));
      } else {
        toast.error(detail || 'Registratie mislukt. Probeer het opnieuw.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex">
      {/* Left side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1671053803599-68155dd2c7b6?crop=entropy&cs=srgb&fm=jpg&w=800"
          alt="Tevreden gasten"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-[#1A4D2E]/80 to-[#1A4D2E]/40"></div>
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-heading text-3xl font-bold mb-4">Sluit je aan bij 5.000+ Restaurants</h2>
          <p className="text-lg text-white/90">
            Start vandaag met het accepteren van online bestellingen en laat je bedrijf groeien met AI-inzichten.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8" data-testid="logo-link">
            <div className="w-10 h-10 bg-[#1A4D2E] rounded-xl flex items-center justify-center">
              <Utensils className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-xl text-[#1A4D2E]">FoodFlows</span>
          </Link>

          <h1 className="font-heading text-3xl font-bold text-[#1F2937] mb-2">Maak je account aan</h1>
          <p className="text-gray-600 mb-8">Start je 14-dagen gratis proefperiode</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Restaurantnaam</Label>
              <Input
                id="name"
                name="name"
                placeholder="Jouw Restaurant Naam"
                value={formData.name}
                onChange={handleChange}
                required
                className="h-12"
                data-testid="name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">E-mailadres</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="jij@restaurant.nl"
                value={formData.email}
                onChange={handleChange}
                required
                className="h-12"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Wachtwoord</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Maak een sterk wachtwoord"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  className="h-12 pr-12"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Telefoonnummer</Label>
              <Input
                id="phone"
                name="phone"
                placeholder="+32 123 456 789"
                value={formData.phone}
                onChange={handleChange}
                className="h-12"
                data-testid="phone-input"
                required
              />
              {veldFouten.phone && <p className="text-sm text-red-600">{veldFouten.phone}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Straat en huisnummer</Label>
              <Input
                id="address"
                name="address"
                placeholder="Dorpsstraat 12"
                value={formData.address}
                onChange={handleChange}
                className="h-12"
                data-testid="address-input"
                required
              />
              {veldFouten.address && <p className="text-sm text-red-600">{veldFouten.address}</p>}
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label htmlFor="postalCode">Postcode</Label>
                <Input
                  id="postalCode"
                  name="postalCode"
                  placeholder="2990"
                  inputMode="numeric"
                  maxLength={4}
                  value={formData.postalCode}
                  onChange={handleChange}
                  className="h-12"
                  data-testid="postalcode-input"
                  required
                />
                {veldFouten.postalCode && <p className="text-sm text-red-600">{veldFouten.postalCode}</p>}
              </div>
              <div className="space-y-2 col-span-2">
                <Label htmlFor="city">Gemeente</Label>
                <Input
                  id="city"
                  name="city"
                  placeholder="Wuustwezel"
                  value={formData.city}
                  onChange={handleChange}
                  className="h-12"
                  data-testid="city-input"
                  required
                />
                {veldFouten.city && <p className="text-sm text-red-600">{veldFouten.city}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="vatNumber">Btw-nummer</Label>
              <Input
                id="vatNumber"
                name="vatNumber"
                placeholder="BE0123456749"
                value={formData.vatNumber}
                onChange={handleChange}
                className="h-12"
                data-testid="vat-input"
                required
              />
              {veldFouten.vatNumber
                ? <p className="text-sm text-red-600">{veldFouten.vatNumber}</p>
                : <p className="text-xs text-gray-500">Je Belgische ondernemingsnummer, bijvoorbeeld BE0123456749</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="cuisineType">Keuken Type (optioneel)</Label>
              <Input
                id="cuisineType"
                name="cuisineType"
                placeholder="Italiaans, Mexicaans, Aziatisch, etc."
                value={formData.cuisineType}
                onChange={handleChange}
                className="h-12"
                data-testid="cuisine-input"
              />
            </div>

            <div className="pt-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={akkoord}
                  onChange={(e) => {
                    setAkkoord(e.target.checked);
                    if (e.target.checked) setVeldFouten({ ...veldFouten, akkoord: null });
                  }}
                  data-testid="akkoord-checkbox"
                  className="mt-1 w-4 h-4 rounded border-gray-300 text-[#1A4D2E] focus:ring-[#1A4D2E]"
                />
                <span className="text-sm text-gray-700 leading-relaxed">
                  Ik ga akkoord met de{' '}
                  <Link to="/voorwaarden" target="_blank"
                        className="text-[#1A4D2E] underline hover:no-underline">
                    algemene voorwaarden</Link>, de{' '}
                  <Link to="/verwerkersovereenkomst" target="_blank"
                        className="text-[#1A4D2E] underline hover:no-underline">
                    verwerkersovereenkomst</Link>{' '}en het{' '}
                  <Link to="/privacybeleid" target="_blank"
                        className="text-[#1A4D2E] underline hover:no-underline">
                    privacybeleid</Link>.
                </span>
              </label>
              {veldFouten.akkoord && (
                <p className="text-sm text-red-600 mt-1">{veldFouten.akkoord}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full btn-primary h-12 mt-6"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? 'Account aanmaken...' : 'Account Aanmaken'}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>


          <p className="mt-6 text-center text-gray-600">
            Heb je al een account?{' '}
            <Link to="/login" className="text-[#1A4D2E] font-medium hover:underline" data-testid="login-link">
              Inloggen
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
